import type { AuthTokens, AuthTokensDto } from '../types/auth.types';

/** Normalize the snake_case wire payload into our camelCase domain model. */
export function mapTokens(dto: AuthTokensDto): AuthTokens {
  return {
    tokenType: dto.token_type,
    expiresIn: dto.expires_in,
    accessToken: dto.access_token,
    refreshToken: dto.refresh_token,
    xZumoAuth: dto.x_zumo_auth ?? null,
    xZumoAuthHosts: normalizeHostTokens(dto.x_zumo_auth_hosts),
  };
}

/** Lower-cased host keys, string tokens only; null when nothing usable. */
function normalizeHostTokens(raw: unknown): Record<string, string> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: Record<string, string> = {};
  for (const [host, token] of Object.entries(raw)) {
    if (typeof token === 'string' && token) out[host.toLowerCase()] = token;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The EasyAuth token the session already holds for the host of `url`. A token
 * is only valid on the host that issued it, so when the session has per-host
 * tokens only that host's entry is used — null for a host without one (not
 * issued yet: `ensureZumoToken()` in `lib/medis/zumoToken.ts` fetches it on
 * demand). Sessions without the map (issued before it existed) keep using the
 * single legacy `xZumoAuth`.
 */
export function zumoTokenFor(tokens: AuthTokens | null | undefined, url?: string): string | null {
  if (!tokens) return null;
  const hosts = tokens.xZumoAuthHosts;
  if (!hosts) return tokens.xZumoAuth ?? null;
  const host = hostOf(url);
  return (host && hosts[host]) || null;
}

/**
 * Host name without the port, because that is how the auth server keys the
 * map (PHP `parse_url(..., PHP_URL_HOST)`) and what `POST /v1/zumo-token`
 * expects: `URL.host` keeps a non-default port and would never match such a
 * key. Null for an unusable URL.
 */
export function hostOf(url?: string): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).hostname.toLowerCase();
  } catch {
    return null;
  }
}
